"use client";

import React, { Suspense, useState, useEffect, useCallback } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { apiRequest } from "../utils/api";
import { ProductCard } from "../components/ProductCard";
import { SlidersHorizontal, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import toast from "react-hot-toast";

const PRODUCTS_PER_PAGE = 8;

const ShopContent: React.FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Filter States
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [minPrice, setMinPrice] = useState(searchParams.get("minPrice") || "");
  const [maxPrice, setMaxPrice] = useState(searchParams.get("maxPrice") || "");
  
  // Sort State
  const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "createdAt");
  const [sortOrder, setSortOrder] = useState(searchParams.get("sortOrder") || "desc");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);

  // Products response state
  const [products, setProducts] = useState<any[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Categories dynamic state
  const [categoriesList, setCategoriesList] = useState<string[]>([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiRequest("/categories?active=true");
        if (response?.success && response.data) {
          setCategoriesList(response.data.map((c: any) => c.name));
        }
      } catch {
      }
    };
    fetchCategories();
  }, []);

  // Update filter states if URL parameters change
  useEffect(() => {
    setSearchTerm(searchParams.get("search") || "");
    setSelectedCategory(searchParams.get("category") || "");
    setMinPrice(searchParams.get("minPrice") || "");
    setMaxPrice(searchParams.get("maxPrice") || "");
    setSortBy(searchParams.get("sortBy") || "createdAt");
    setSortOrder(searchParams.get("sortOrder") || "desc");
    setCurrentPage(Number(searchParams.get("page")) || 1);
  }, [searchParams]);

  // Fetch products based on state variables
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.set("searchTerm", searchTerm);
      if (selectedCategory) queryParams.set("category", selectedCategory);
      if (minPrice) queryParams.set("minPrice", minPrice);
      if (maxPrice) queryParams.set("maxPrice", maxPrice);
      
      queryParams.set("sortBy", sortBy);
      queryParams.set("sortOrder", sortOrder);
      queryParams.set("page", currentPage.toString());
      queryParams.set("limit", PRODUCTS_PER_PAGE.toString());

      const response = await apiRequest(`/products?${queryParams.toString()}`);
      if (response?.success && response.data) {
        setProducts(response.data);
        setTotalProducts(response.meta?.total || 0);
      }
    } catch {
      toast.error("Failed to load products.");
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, selectedCategory, minPrice, maxPrice, sortBy, sortOrder, currentPage]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Update query params in the URL
  const updateUrl = (updates: Record<string, string | number | null>) => {
    const params = new URLSearchParams(searchParams.toString());
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === "") {
        params.delete(key);
      } else {
        params.set(key, value.toString());
      }
    });

    // Reset to page 1 on filter changes unless page itself is updated
    if (!updates.hasOwnProperty("page")) {
      params.set("page", "1");
    }

    router.push(`/shop?${params.toString()}`);
  };

  const handleClearFilters = () => {
    router.push("/shop");
  };

  const totalPages = Math.ceil(totalProducts / PRODUCTS_PER_PAGE) || 1;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 w-full flex-1">
      <div className="flex flex-col md:flex-row gap-8">
        
        {/* Sidebar Filters */}
        <aside className="w-full md:w-64 shrink-0 space-y-6">
          <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 pb-4">
            <h2 className="flex items-center gap-2 text-md font-bold text-zinc-900 dark:text-white">
              <SlidersHorizontal className="h-4 w-4" />
              Filter Products
            </h2>
            <button
              onClick={handleClearFilters}
              className="text-xs flex items-center gap-1 text-zinc-500 hover:text-indigo-500 transition-colors"
            >
              <RotateCcw className="h-3 w-3" />
              Reset All
            </button>
          </div>

          {/* Search bar */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Search Keywords</h3>
            <input
              type="text"
              placeholder="e.g. iPhone, Laptop..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                updateUrl({ search: e.target.value });
              }}
              className="w-full px-3 py-1.5 text-sm rounded bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Categories</h3>
            <div className="flex flex-col gap-1.5">
              {categoriesList.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    const nextVal = selectedCategory === cat ? null : cat;
                    setSelectedCategory(nextVal || "");
                    updateUrl({ category: nextVal });
                  }}
                  className={`text-left text-sm py-1 px-2 rounded-md transition-all ${
                    selectedCategory.toLowerCase() === cat.toLowerCase()
                      ? "bg-violet-600 text-white font-semibold"
                      : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-900"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Price Bounds</h3>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                placeholder="Min (Tk)"
                value={minPrice}
                onChange={(e) => {
                  setMinPrice(e.target.value);
                  updateUrl({ minPrice: e.target.value || null });
                }}
                className="w-1/2 px-3 py-1.5 text-xs rounded bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <span className="text-zinc-400 text-xs">to</span>
              <input
                type="number"
                placeholder="Max (Tk)"
                value={maxPrice}
                onChange={(e) => {
                  setMaxPrice(e.target.value);
                  updateUrl({ maxPrice: e.target.value || null });
                }}
                className="w-1/2 px-3 py-1.5 text-xs rounded bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </aside>

        {/* Products Grid & Toolbar */}
        <main className="flex-1 space-y-6">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-zinc-200 dark:border-zinc-800 pb-4">
            <div className="text-sm text-zinc-500">
              Showing <span className="font-semibold text-zinc-900 dark:text-white">{products.length}</span> of{" "}
              <span className="font-semibold text-zinc-900 dark:text-white">{totalProducts}</span> products
            </div>
            
            {/* Sort controls */}
            <div className="flex items-center gap-2 self-stretch sm:self-auto">
              <label className="text-xs text-zinc-500 font-medium shrink-0">Sort By</label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split("-");
                  setSortBy(field);
                  setSortOrder(order);
                  updateUrl({ sortBy: field, sortOrder: order });
                }}
                className="w-full sm:w-auto text-xs rounded border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-zinc-700 dark:text-zinc-200"
              >
                <option value="createdAt-desc">Newest First</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
              </select>
            </div>
          </div>

          {/* Grid View */}
          {isLoading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse flex flex-col gap-4 border border-zinc-200 dark:border-zinc-900 p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950">
                  <div className="aspect-square bg-zinc-200 dark:bg-zinc-900 rounded-xl" />
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-900 rounded-md w-2/3" />
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-900 rounded-md w-1/2 mt-auto" />
                </div>
              ))}
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-zinc-200 dark:border-zinc-800 pt-6">
                  <button
                    onClick={() => {
                      const prevPage = Math.max(1, currentPage - 1);
                      setCurrentPage(prevPage);
                      updateUrl({ page: prevPage });
                    }}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-zinc-700 dark:text-zinc-300 disabled:opacity-50 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  <span className="text-sm font-medium text-zinc-500">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => {
                      const nextPage = Math.min(totalPages, currentPage + 1);
                      setCurrentPage(nextPage);
                      updateUrl({ page: nextPage });
                    }}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-zinc-700 dark:text-zinc-300 disabled:opacity-50 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl">
              <h3 className="text-lg font-bold text-zinc-900 dark:text-white">No products match these parameters</h3>
              <p className="text-sm text-zinc-500 mt-2">
                Try clearing some filters or searching for alternative tech gear.
              </p>
              <button
                onClick={handleClearFilters}
                className="mt-6 rounded-full bg-violet-600 px-6 py-2 text-sm font-semibold text-white hover:bg-violet-500 transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default function ShopPage() {
  return (
    <Suspense fallback={
      <div className="flex-1 flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
      </div>
    }>
      <ShopContent />
    </Suspense>
  );
}
